import React, { useEffect, useState } from "react";
import { profile } from "@/lib/data";
import { Download, ArrowRight, MapPin } from "lucide-react";

const TYPED = [
    "Senior Software Engineer",
    "Distributed Systems Architect",
    "Generative AI Engineer",
];

function useTyped(words, speed = 70, pause = 1400) {
    const [text, setText] = useState("");
    const [idx, setIdx] = useState(0);
    const [del, setDel] = useState(false);

    useEffect(() => {
        const current = words[idx % words.length];
        const next = del
            ? current.slice(0, text.length - 1)
            : current.slice(0, text.length + 1);

        const delay = del ? speed / 2 : speed;

        const timer = setTimeout(() => {
            setText(next);
            if (!del && next === current) {
                setTimeout(() => setDel(true), pause);
            } else if (del && next === "") {
                setDel(false);
                setIdx((i) => i + 1);
            }
        }, delay);
        return () => clearTimeout(timer);
    }, [text, del, idx, words, speed, pause]);

    return text;
}

export default function Hero() {
    const typed = useTyped(TYPED);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
        >
            {/* Background grid */}
            <div className="absolute inset-0 terminal-grid opacity-60 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none [background:radial-gradient(ellipse_at_top_left,rgba(34,197,94,0.18),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(34,197,94,0.08),transparent_50%)]" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                {/* Left column — terminal window + identity */}
                <div className="lg:col-span-7 space-y-8 reveal-up">
                    {/* Terminal mock */}
                    <div
                        data-testid="hero-terminal"
                        className="border border-term-green/30 bg-black/70 backdrop-blur-sm"
                    >
                        <div className="flex items-center justify-between px-4 py-2 border-b border-term-green/20 bg-black/60">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-term-rose/80" />
                                <span className="w-2.5 h-2.5 rounded-full bg-term-amber/80" />
                                <span className="w-2.5 h-2.5 rounded-full bg-term-green/80" />
                            </div>
                            <span className="text-[10px] font-mono text-ink-tertiary uppercase tracking-[0.2em]">
                                manoj@portfolio — zsh — 80×24
                            </span>
                            <span className="w-12" />
                        </div>
                        <div className="px-4 sm:px-6 py-5 font-mono text-sm space-y-2">
                            <div className="text-ink-tertiary">
                                <span className="text-term-green">➜</span>{" "}
                                <span className="text-term-cyan">~/portfolio</span>{" "}
                                <span className="text-ink-secondary">whoami</span>
                            </div>
                            <div className="text-ink-primary pl-4">manoj_suthar</div>
                            <div className="text-ink-tertiary">
                                <span className="text-term-green">➜</span>{" "}
                                <span className="text-term-cyan">~/portfolio</span>{" "}
                                <span className="text-ink-secondary">
                                    cat about.json
                                </span>
                            </div>
                            <pre className="pl-4 text-ink-secondary whitespace-pre-wrap leading-relaxed text-[12.5px] sm:text-[13px]">
{`{
  "role":     "${typed}`}<span className="cursor-blink text-term-green">▍</span>{`",
  "years":    "5+",
  "stack":    ["Java", "Spring Boot", "Kafka", "K8s"],
  "focus":    ["Microservices", "Distributed Systems", "GenAI"],
  "status":   "open to senior backend / platform roles"
}`}
                            </pre>
                        </div>
                    </div>

                    {/* Headline */}
                    <div className="space-y-5">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-term-green">
                            <span className="h-px w-8 bg-term-green/60" />
                            <span>// available for opportunities</span>
                        </div>
                        <h1
                            data-testid="hero-name"
                            className="font-mono font-black tracking-tighter leading-[0.95] text-ink-primary text-5xl sm:text-6xl lg:text-7xl"
                        >
                            {profile.name}
                            <span className="text-term-green">.</span>
                        </h1>
                        <p className="font-display text-lg sm:text-xl text-ink-secondary max-w-2xl leading-relaxed">
                            Building <span className="text-ink-primary">high-throughput, cloud-native microservices</span> at scale —
                            Java · Spring Boot · Kafka · Kubernetes. Actively shipping{" "}
                            <span className="text-term-green">Generative AI</span> into production tooling.
                        </p>

                        <div className="flex flex-wrap items-center gap-3 pt-2">
                            <a
                                href={profile.resumeUrl}
                                download
                                data-testid="hero-resume-download"
                                className="group inline-flex items-center gap-2 px-5 py-3 bg-term-green text-bg-base font-mono text-sm font-semibold uppercase tracking-[0.14em] hover:bg-term-green-hi active:translate-y-[2px] transition-all"
                            >
                                <Download className="w-4 h-4" />
                                download_resume.pdf
                            </a>
                            <a
                                href="#contact"
                                data-testid="hero-contact-btn"
                                className="group inline-flex items-center gap-2 px-5 py-3 border border-term-green/50 text-term-green font-mono text-sm font-semibold uppercase tracking-[0.14em] hover:bg-term-green hover:text-bg-base active:translate-y-[2px] transition-all"
                            >
                                get_in_touch
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 font-mono text-xs text-ink-tertiary">
                            <span className="inline-flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5 text-term-green" />
                                Based in India · Remote-ready
                            </span>
                            <span>
                                <span className="text-term-green">●</span> 5+ years
                                experience
                            </span>
                            <span>
                                <span className="text-term-green">●</span> Nagarro · Capiter
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right column — stats panel */}
                <div className="lg:col-span-5 space-y-4">
                    <div
                        data-testid="hero-stats"
                        className="border border-term-green/20 bg-bg-surface/60 backdrop-blur-sm divide-y divide-term-green/10"
                    >
                        <StatRow label="// uptime" value="99.9%" hint="production SLA" />
                        <StatRow
                            label="// daily transactions"
                            value="500K+"
                            hint="microservices"
                        />
                        <StatRow
                            label="// monthly checkout"
                            value="$2M+"
                            hint="SNPL @ Capiter"
                        />
                        <StatRow
                            label="// deploy lead-time"
                            value="↓60%"
                            hint="Jenkins + K8s"
                        />
                        <StatRow
                            label="// post-release defects"
                            value="↓50%"
                            hint=">85% test coverage"
                        />
                    </div>

                    <div className="border border-term-green/20 bg-bg-surface/60 backdrop-blur-sm p-5 font-mono text-xs">
                        <div className="text-term-green uppercase tracking-[0.22em] mb-3">
                            // certifications
                        </div>
                        <ul className="space-y-1.5 text-ink-secondary">
                            <li>
                                <span className="text-term-green">[+]</span> Oracle Certified
                                Generative AI Professional
                            </li>
                            <li>
                                <span className="text-term-green">[+]</span> AI Agents in
                                Java w/ Generative AI
                            </li>
                            <li>
                                <span className="text-term-green">[+]</span> DevSecOps
                                Technical Basics
                            </li>
                            <li>
                                <span className="text-term-green">[+]</span> Certified Vaadin
                                24 Developer
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function StatRow({ label, value, hint }) {
    return (
        <div className="flex items-baseline justify-between px-5 py-4 hover:bg-term-green/5 transition-colors">
            <div className="space-y-1">
                <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-term-green">
                    {label}
                </div>
                <div className="text-[11px] font-mono text-ink-tertiary">{hint}</div>
            </div>
            <div className="font-mono text-2xl sm:text-3xl font-bold text-ink-primary tabular-nums">
                {value}
            </div>
        </div>
    );
}
